import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Converte um `Date` para string `yyyy-MM-dd` usando os componentes locais (sem fuso UTC). */
export function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** `yyyy-MM-ddTHH:mm` local — mesmo formato que os campos do form produzem quando combinados. */
export function toLocalIsoDateTime(date: Date): string {
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${toLocalIsoDate(date)}T${hours}:${minutes}`;
}

function valorDe(control: AbstractControl | null): string {
  return (control?.value as string | undefined)?.trim() ?? '';
}

/** Data e horário alterados em conjunto: mexer só no horário também revalida o par. */
function parAlterado(data: AbstractControl | null, horario: AbstractControl | null): boolean {
  return Boolean(data?.dirty) || Boolean(horario?.dirty);
}

export function campanhaVigenciaValidator(
  referenceDate: () => Date = () => new Date(),
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dataInicioControl = control.get('dataInicio');
    const dataFimControl = control.get('dataFim');
    const horarioInicioControl = control.get('horarioInicio');
    const horarioFimControl = control.get('horarioFim');

    const dataInicio = valorDe(dataInicioControl);
    const dataFim = valorDe(dataFimControl);
    const horarioInicio = valorDe(horarioInicioControl);
    const horarioFim = valorDe(horarioFimControl);

    const agora = referenceDate();
    const hoje = toLocalIsoDate(agora);
    const instanteAtual = toLocalIsoDateTime(agora);

    const errors: ValidationErrors = {};

    // Com os dois horários preenchidos a comparação é de instante; sem eles, continua sendo de
    // data, senão um horário ainda não digitado viraria meia-noite e acusaria ordem invertida.
    if (dataInicio && dataFim) {
      const comHorarios = Boolean(horarioInicio && horarioFim);
      const inicio = comHorarios ? `${dataInicio}T${horarioInicio}` : dataInicio;
      const fim = comHorarios ? `${dataFim}T${horarioFim}` : dataFim;

      // Instantes iguais não formam vigência: o fim tem de ser estritamente posterior. A
      // igualdade só é cobrada com os horários em mãos — uma campanha que começa e termina no
      // mesmo dia é legítima enquanto as horas ainda não foram digitadas.
      const invalida = comHorarios ? fim <= inicio : fim < inicio;

      if (invalida) {
        errors['dataFimBeforeDataInicio'] = true;
      }
    }

    // Só o que a pessoa mexeu é cobrado: uma campanha antiga aberta para edição não pode
    // acusar erro de passado apenas por ter sido carregada.
    if (parAlterado(dataInicioControl, horarioInicioControl) && dataInicio) {
      // Com horário, o início precisa ser estritamente futuro: marcar o minuto corrente já
      // nasce vencido. Sem horário, só a data é cobrada — hoje continua valendo, porque a hora
      // que a pessoa ainda vai digitar é que decide.
      const noPassado = horarioInicio
        ? `${dataInicio}T${horarioInicio}` <= instanteAtual
        : dataInicio < hoje;

      if (noPassado) {
        errors['dataInicioNoPassado'] = true;
      }
    }

    if (parAlterado(dataFimControl, horarioFimControl) && dataFim) {
      const noPassado = horarioFim
        ? `${dataFim}T${horarioFim}` < instanteAtual
        : dataFim < hoje;

      if (noPassado) {
        errors['dataFimNoPassado'] = true;
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}
